import React, { useState, useEffect, useContext } from "react";
import NumberSelector from '../Controls/Selectors/NumberSelector';
import { ModalCloseContext } from "../Controls/Button/ModalButton";
import "./css/UserSettings.css";

export const policyDisclaimerAcceptedSetting = 'policy-disclaimer-accepted';
export const defaultPageCountSetting = 'default-page-count';
export const defaultframeIndexSetting = 'default-frame-index';
export const defaultPaperSizeSetting = "default-paper-size";
export const allowSkitterbookSelectionName = "allow-skitterbook-deletion";
export const useFlipVibrationName = "use-flip-vibration";
export const visualThemeName = "visual-theme-name";

export type PaperSize = "A4" | "Letter";
export type ThemeName = "light" | "dark";

export type UserSettingsData = {
    hasShownIntroduction: boolean;
    hasAcceptedDisclaimer: boolean;
    allowSkitterbookDeletion: boolean;
    useFlipVibration: boolean;
    defaultSkitterbookPages: number;
    defaultSkitterbookFrameInterval: number;
    paperSize: PaperSize;
    visualTheme: ThemeName;
};

type Props = {
    initial: UserSettingsData;
    onSave: (settings: UserSettingsData) => void;
};

export function UserSettings({ initial, onSave }: Props) {
    const [settings, setSettings] = useState<UserSettingsData>(initial);
    const [isVibrateEnabled, setIsVibrateEnabled] = useState(true);

    const closeModal = useContext(ModalCloseContext);

    useEffect(() => {
        if (!("vibrate" in navigator)) {
            setIsVibrateEnabled(false);
        }
    }, []);

    const update = <K extends keyof UserSettingsData>(
        key: K,
        value: UserSettingsData[K]
    ) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = () => {
        onSave(settings);
        if (closeModal) {
            closeModal();
        }
    };

    return (
        <div className="user-settings">
            <div className="user-settings-group">
                <label className="user-settings-label">
                    <input
                        type="checkbox"
                        checked={settings.hasShownIntroduction}
                        onChange={e => update("hasShownIntroduction", e.target.checked)}
                    />
                    Has Shown Introduction
                </label>
            </div>

            <div className="user-settings-group">
                <label className="user-settings-label">
                    <input
                        type="checkbox"
                        checked={settings.hasAcceptedDisclaimer}
                        onChange={e => update("hasAcceptedDisclaimer", e.target.checked)}
                    />
                    Has Accepted Disclaimer
                </label>
            </div>

            <div className="user-settings-group">
                <label className="user-settings-label">
                    Default Skitterbook Pages
                </label>
                <NumberSelector
                    min={40}
                    max={80}
                    step={10}
                    selectedColor="#1957ea"
                    unselectedColor="#E0E0E0"
                    numberTextColor="#000000"
                    barColor="transparent"
                    initialSelected={settings.defaultSkitterbookPages}   // controlled
                    onChange={(val: number) => update("defaultSkitterbookPages", val)} />
            </div>

            <div className="user-settings-group">
                <label className="user-settings-label">
                    Default Skitterbook Frame Interval
                </label>
                <NumberSelector
                    min={1}
                    max={5}
                    step={1}
                    selectedColor="#1957ea"
                    unselectedColor="#E0E0E0"
                    numberTextColor="#000000"
                    barColor="#F5F5F5"
                    initialSelected={settings.defaultSkitterbookFrameInterval}  // controlled
                    onChange={(val: number) => update("defaultSkitterbookFrameInterval", val)} />
            </div>

            <div className="user-settings-group">
                <label className="user-settings-label">Visual Theme</label>
                <select className="user-settings-select" value={settings.visualTheme} onChange={e => update("visualTheme", e.target.value as ThemeName)}>
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                </select>
            </div>

            <div className="user-settings-group">
                <label className="user-settings-label">Paper Size</label>
                <select className="user-settings-select" value={settings.paperSize} onChange={e => update("paperSize", e.target.value as PaperSize)}>
                    <option value="A4">A4</option>
                    <option value="Letter">Letter</option>
                </select>
            </div>

            <div className="user-settings-group">
                <label className="user-settings-label">
                    <input
                        type="checkbox"
                        checked={settings.allowSkitterbookDeletion}
                        onChange={e => update("allowSkitterbookDeletion", e.target.checked)}
                    />
                    Allow Skitterbook deletion
                </label>
            </div>

            <div className="user-settings-group">
                <label className={`user-settings-label${isVibrateEnabled ? '' : ' disabled'}`}>
                    <input
                        type="checkbox"
                        disabled={!isVibrateEnabled}
                        checked={settings.useFlipVibration}
                        onChange={e => update("useFlipVibration", e.target.checked)}
                    />
                    Use Flip Vibration
                </label>
            </div>

            <button className="user-settings-save" onClick={handleSave}>
                Save and Close
            </button>
        </div>
    );
}
